/**
 * The two identity chips, once.
 *
 * A player is named by two facts that never change with the screen he is on: the club he plays
 * for and the position he plays. Both were being drawn from the same two tables in four different
 * files, each writing out its own `hsl()` pair or reaching into `NFL_CHIPS` for a hex — which is
 * how a keeper row, a submit sheet and a pick summary ended up agreeing by coincidence rather
 * than by construction. `handoff/chip-system.md` is normative and says there is one chip grammar
 * across the app; this is where it lives.
 *
 * WHAT MAKES THEM ONE THING IS THE COMPONENT, NOT THE CLASS. `.kchip` alone would still let a
 * caller pass its own colours, and the drift that started this was in the colours rather than in
 * the box. So the hue table and the club table are read HERE and nowhere else — a screen names a
 * position and gets the position's colour, or names a club and gets the club's, and there is no
 * third option. `tests/chips.test.ts` fails on a chip hex written anywhere else.
 *
 * The status chip — Farm, Dropped, Sleeper — is the third of the three and is not here: it is
 * tappable, carries a callout, and lives with the thing that owns its behaviour
 * (`ui/keeper/Callout.tsx`). Identity is a colour; status is a colour and an explanation.
 */

import { NFL_CHIPS, nflInfoOf } from './NflChip.js';
import { Callout } from './keeper/Callout.js';

/**
 * Position hues. Identity, not status — a pale background under dark text of the same hue, which
 * is the opposite of how this app draws a warning, and deliberately so.
 *
 * QB 210 · RB 150 · WR 30 · TE 265, from `handoff/chip-system.md`. DEF is not in that table
 * because no defence appears on a chip in the drawings, but one can be kept and the pick summary
 * has to be able to name it; 250 keeps it clear of TE.
 */
export const POS_HUE: Readonly<Record<string, number>> = {
  QB: 210, RB: 150, WR: 30, TE: 265, DEF: 250,
};

/** Dark text on a light wash of the same hue — the one formula, applied in one place. */
const posStyle = (hue: number) => ({
  background: `hsl(${hue} 62% 91%)`,
  color: `hsl(${hue} 45% 30%)`,
});

export function PositionChip(
  { pos, className }: { pos: string | null | undefined; className?: string },
) {
  if (!pos) return null;
  const hue = POS_HUE[pos];
  /*
   * An unmapped position draws nothing rather than a grey chip. A silent grey fallback is what
   * `chip-system.md` calls a bug rather than a default — it looks like a design decision and
   * hides the missing row. Nothing renders, and the gap is visible.
   */
  if (hue === undefined) return null;
  return (
    <span className={`kchip pos${className ? ` ${className}` : ''}`} style={posStyle(hue)}>
      {pos}
    </span>
  );
}

/**
 * The club chip: full-strength club colour as the fill, the secondary as a 2px right edge, white
 * ink. The one sanctioned exception to the app's identity-colour rule, and the reasoning for it
 * is at the top of `NflChip.tsx` — a club in a pastel wash stops being recognisable as the club.
 */
export function ClubChip(
  { team, title, className }: { team: string | null | undefined; title?: string; className?: string },
) {
  if (!team) return null;
  const c = NFL_CHIPS[team];
  if (!c) return null;
  return (
    <span className={`kchip club${className ? ` ${className}` : ''}`}
      title={title ?? team}
      style={{ background: c.fill, borderRightColor: c.edge }}>
      {team}
    </span>
  );
}

/**
 * The club chip for a player named rather than a club named — the common case on any roster
 * surface, where what is in hand is "Puka Nacua" and not "LAR".
 *
 * A free agent or a retired player has no club and draws nothing here. That is different from
 * `NflMark`, which draws a grey FA/RET marker: on a draft board the absence is the news, and on a
 * name line beside a position chip it is a third chip nobody asked for.
 */
export function PlayerClubChip({ player, className }: { player: string; className?: string }) {
  const info = nflInfoOf(player);
  if (!info?.team) return null;
  return (
    <ClubChip team={info.team}
      title={info.bye ? `${info.team} · bye ${info.bye}` : info.team}
      {...(className ? { className } : {})} />
  );
}

/* ============================================================================================
 * THE THREE THE FARM SCREEN ASKS FOR
 *
 * REBUILT 21 August, after the originals were lost. Three sessions were writing this tree at once
 * and this file was overwritten with a copy taken before the Farm pass landed; the Farm CSS went
 * the same way and came back from Dropbox's version history, but this file did not. Nothing here
 * is invented: the shape is pinned from three directions — the CSS contract still in `styles.css`
 * (`.kcowrap` > `.kcobtn` > `.kchip.status.<tone>`), the props `FarmLog.tsx` passes, and
 * `tests/farmChrome.test.ts` plus `farmcheck.mjs`, which measure the result. If those pass, this
 * is the component that was lost.
 *
 * The chip colours are NOT here and must not come here. Position hues are the table above; club
 * and college colours are `NflChip.tsx` and `CollegeChip.tsx`; outcome tones are three CSS rules
 * and the only place those colours appear. `farmChrome.test.ts` fails on a fourth home for any
 * of them.
 * ========================================================================================== */

/**
 * The college chip, re-exported rather than re-implemented.
 *
 * It lives in `CollegeChip.tsx` with the 200-odd school table it reads, and the Farm screen wants
 * it from the same place it gets a position chip. One import site, one chip grammar.
 */
export { CollegeChip } from './CollegeChip.js';

/** The position chip under the name the Farm screen calls it by. */
export function PosChip({ pos }: { pos: string | null | undefined }) {
  return <PositionChip pos={pos} />;
}

/**
 * The outcome chip — the third of the three chip classes, and the only one that answers back.
 *
 * Outlined rather than filled, so it cannot be read as a position (tinted wash) or a club
 * (saturated fill). What it says is short by necessity — "Drafted", "In college", "Rights ended"
 * — and the story behind it is a tap away in the same dark bubble every status chip in this app
 * uses, which is why this is a `Callout` and not a `<span>`.
 *
 * The 44px hit box comes from `.kcobtn`, with the collapsing negative margin that keeps an 18px
 * chip in a 32px row while giving a finger the whole 44. `farmcheck.mjs` measures the BUTTON, not
 * the wrapper — the wrapper's box is the margin box and is deliberately chip-sized.
 */
export function OutcomeChip(
  { label, tone, detail, ariaLabel }:
  { label: string; tone: string; detail: string; ariaLabel?: string },
) {
  return (
    <Callout
      label={label}
      text={detail}
      tone={`status ${tone}`}
      {...(ariaLabel ? { ariaLabel } : {})}
    />
  );
}
